import { useState, useRef, useEffect } from "react";
import { Activity } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { verifyOtp } from "../api";
import bgLogo from "../assets/bg.svg";
import heroImage from "../assets/signal-ai-hero.jpg";

const STORAGE_KEY = "signalai_assessment_data";
const REPORT_COPY_STORAGE_KEY = "signalai_report_copy";
const ASSESSMENT_ID_STORAGE_KEY = "signalai_assessment_id";

function SignalAIOTPVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, company, answers, assessment_id } = (location.state || {}) as {
    user?: { email?: string };
    company?: unknown;
    answers?: unknown;
    assessment_id?: string | number;
  };

  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (timer <= 0) return;
    const id = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [timer]);

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    setError("");
    if (digit && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const next = [...otp];
        next[index] = "";
        setOtp(next);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setOtp(next);
    const focusIndex = Math.min(pasted.length, 5);
    inputsRef.current[focusIndex]?.focus();
  };

  const isComplete = otp.every((d) => d !== "");

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isComplete) return;

    if (!user?.email) {
      setError("Missing email context. Please restart from user details page.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const verifyResult = await verifyOtp({
        email: user.email,
        otp: otp.join(""),
      });

      if (typeof window !== "undefined") {
        if (verifyResult?.assessment_data) {
          window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(verifyResult.assessment_data));
        }
        if (verifyResult?.report_copy) {
          window.sessionStorage.setItem(REPORT_COPY_STORAGE_KEY, JSON.stringify(verifyResult.report_copy));
        }
        window.sessionStorage.setItem(
          ASSESSMENT_ID_STORAGE_KEY,
          String(verifyResult?.assessment_id || assessment_id || "")
        );
      }

      setLoading(false);
      navigate("/signalai-thank-you", {
        state: {
          user,
          company,
          answers,
          assessment_id: verifyResult?.assessment_id || assessment_id,
          assessmentData: verifyResult?.assessment_data,
          reportCopy: verifyResult?.report_copy,
        },
      });
    } catch (_err) {
      setError("Verification failed. Please try again.");
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (timer > 0) return;
    setOtp(["", "", "", "", "", ""]);
    setError("");
    setTimer(30);
    inputsRef.current[0]?.focus();
    console.log("OTP resent");
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center bg-gradient-hero px-4 py-10 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-mesh opacity-60 pointer-events-none" aria-hidden />
      <div className="relative w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">

        {/* Left visual panel */}
        <div className="hidden lg:flex w-full flex-col gap-6">
          <div className="relative w-full rounded-2xl overflow-hidden shadow-elevated border border-border">
            <img
              src={heroImage}
              alt="Signal AI readiness dashboard preview"
              className="w-full h-[420px] object-cover"
            />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#0d1117] px-3 py-1.5 rounded-full text-xs font-semibold text-white shadow-sm border border-white/10">
              <span className="w-2 h-2 rounded-full bg-signal animate-pulse" />
              Signal AI Readiness Report
            </div>
          </div>
          <div className="relative w-full overflow-hidden rounded-2xl border border-slate-800/60 bg-gradient-to-r from-[#020b1f] via-[#0a1833] to-[#111f39] p-7 shadow-elevated">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(39,148,255,0.18),transparent_45%)]" aria-hidden />
            <p className="relative text-xl font-medium leading-relaxed text-slate-100">
              Every $ you invest in AI should return more - Signal AI shows you where that actually happens.
            </p>
          </div>
        </div>

        {/* Right form card */}
        <div className="flex w-full justify-center">
          <div className="relative w-full max-w-[520px] bg-card border border-border rounded-2xl shadow-elevated p-8 sm:p-10">
        <div className="mb-6 flex w-full items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-dark">
              <Activity className="h-4 w-4 text-signal" strokeWidth={2.5} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-base font-semibold tracking-tight text-foreground">Signal AI</span>
              <span className="inline-flex items-center gap-0">
                <span className="text-xs font-medium text-muted-foreground">by</span>
                <img src={bgLogo} alt="Gowitek" className="h-5 w-auto object-contain" />
              </span>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-[#0d1117] text-white text-base font-bold tracking-wide border border-white/10 whitespace-nowrap shadow-sm">
            Report at $0
          </span>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
            Verify your email
          </h1>
          <p className="mt-3 text-slate-600 text-sm leading-relaxed">
            We've sent a 6-digit verification code to your email.
          </p>
          <p className="mt-2 text-xs text-slate-400">
            If you don't see it, check <span className="font-bold text-black">Spam</span> or <span className="font-bold text-black">Junk</span> folder.
          </p>
        </div>

        <form onSubmit={handleVerify}>
          <div className="flex justify-center gap-2 sm:gap-3 mb-3">
            {otp.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputsRef.current[i] = el;
                }}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                onPaste={handlePaste}
                className={`w-11 h-14 sm:w-12 sm:h-14 text-center text-xl font-semibold text-slate-900 bg-white border rounded-lg outline-none transition focus:ring-2 focus:ring-sky-500 focus:border-sky-500 ${
                  error
                    ? "border-red-300"
                    : digit
                      ? "border-sky-400"
                      : "border-slate-200"
                }`}
              />
            ))}
          </div>

          {error && (
            <p className="text-center text-xs text-red-500 mb-3">{error}</p>
          )}

          <div className="flex items-center justify-center gap-1.5 text-xs text-slate-500 mb-5">
            <span>Didn't receive the code?</span>
            <button
              type="button"
              onClick={handleResend}
              disabled={timer > 0}
              className={`font-medium transition ${
                timer > 0
                  ? "text-slate-400 cursor-not-allowed"
                  : "text-sky-600 hover:text-sky-700"
              }`}
            >
              {timer > 0 ? `Resend in ${timer}s` : "Resend OTP"}
            </button>
          </div>

          <button
            type="submit"
            disabled={!isComplete || loading}
            className={`w-full h-[52px] rounded-xl text-white text-sm font-semibold tracking-wide transition shadow-sm flex items-center justify-center gap-2 ${
              isComplete && !loading
                ? "bg-sky-500 hover:bg-sky-600 active:bg-sky-700 shadow-sky-500/20"
                : "bg-slate-300 cursor-not-allowed"
            }`}
          >
            {loading ? (
              <>
                <span className="h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify OTP"
            )}
          </button>
        </form>

        <div className="mt-6 text-center space-y-1">
          <p className="text-xs font-medium text-slate-700">
            <span className="font-bold text-black">Report at $0.</span> No upfront commitment.
          </p>
          <p className="text-xs text-slate-400">
            You'll receive a reviewed AI readiness view not an instant generic score.
          </p>
        </div>

        <div className="mt-8 pt-5 border-t border-slate-100 flex items-center justify-center gap-2 text-[11px] text-slate-400 tracking-wide uppercase">
          <div className="signal-chip mb-6 lowercase">
               consulting led • ai-first • roi driven
          </div>
        </div>
        </div>{/* end form card */}
        </div>{/* end right panel */}
      </div>{/* end grid */}
    </div>
  );
}

export default SignalAIOTPVerification;
